import { Link, useNavigate } from "react-router-dom";
import { lazy, Suspense, useMemo } from "react";
import { ArrowRight, Search, Shield, Calendar, Users, Home, CheckCircle, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import PropertyCard from "@/components/PropertyCard";
import AlertSignup from "@/components/alerts/AlertSignup";
import { useProperties } from "@/hooks/useProperties";
import { useMostViewedProperties } from "@/hooks/useMostViewedProperties";
import { usePromotions } from "@/lib/monetization";
import { useFavorites } from "@/hooks/useFavorites";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { useCopyPage } from "@/hooks/useSiteCopy";
import { seoDescriptionKey, seoTitleKey } from "@/lib/siteCopy";
import AuthPromptDialog from "@/components/AuthPromptDialog";
import Seo from "@/components/Seo";
import { organizationSchema, webSiteSchema, realEstateAgentSchema, siteFaqSchema } from "@/lib/seo";
import { useAreas } from "@/hooks/useAreas";
import { useBrowseContext } from "@/hooks/useBrowseContext";
import { useCollections } from "@/hooks/useCollections";
import CollectionLinks from "@/components/collections/CollectionLinks";
import { setIntent } from "@/lib/onboardingIntent";
// vite-imagetools generates responsive variants at build time:
//   ?w=800;1200      → two sizes via srcset
//   format=webp      → modern format (~30% smaller than JPG)
//   as=srcset        → produces a "url 800w, url 1200w" string
// Browser picks the best size based on the <img sizes> attribute.
// renterFeature / ownerFeature / heroBg are now resolved at runtime
// via useSiteAsset, admins can swap them via the Site assets panel.
// The hook still ships bundled vite-imagetools fallbacks (the same
// files that were previously imported here) when no upload exists.
import { useSiteAsset } from "@/hooks/useSiteAsset";
import { useShareImage } from "@/hooks/useShareImage";
import { FRAME } from "@/lib/siteAssets";
import { transformImage } from "@/lib/imageUrl";
import StatBar from "@/components/StatBar";
import HeroVideo from "@/components/HeroVideo";

// The concierge (chat, listing sheet, profile dialog, phone and country
// pickers) is most of the home page's code and starts as one small pill
// below the fold, so it downloads after the page has painted.
const ConciergeChat = lazy(() => import("@/components/concierge/ConciergeChat"));

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenant = useTenant();
  // Every word on this page is admin-editable (Admin → Site copy → Home).
  const copy = useCopyPage("home");
  const { data: properties = [] } = useProperties();
  const { collections } = useCollections();
  const browseCtx = useBrowseContext(properties);
  const { data: mostViewed = [] } = useMostViewedProperties(4);
  const promotions = usePromotions();
  // Paid featured homes first, then the most viewed, four in all (Oct 20).
  const featured = useMemo(() => {
    const paid = properties.filter((p) => promotions.featured.has(p.id));
    const ids = new Set(paid.map((p) => p.id));
    return [...paid, ...mostViewed.filter((p) => !ids.has(p.id))].slice(0, 4);
  }, [properties, promotions, mostViewed]);
  const { toggleFavorite, isFavorite, authPromptOpen, setAuthPromptOpen } = useFavorites();
  const heroAsset = useSiteAsset("home.hero", { width: 1920 });
  // The hero is the largest thing on the page; phones get a phone-sized copy
  // (four widths, the browser picks) instead of the 1920 px desktop one.
  const heroSrcSet = !heroAsset.isFallback && heroAsset.objectUrl
    ? [640, 960, 1280, 1920].map((w) => `${transformImage(heroAsset.objectUrl!, { width: w })} ${w}w`).join(", ")
    : undefined;
  const heroVideoAsset = useSiteAsset("home.heroVideo");
  // Prefer the video when admin has uploaded one, falls back to the
  // image hero (and ultimately the bundled static) otherwise.
  const useHeroVideo = !heroVideoAsset.isFallback && heroVideoAsset.mediaType === "video";
  const renterAsset = useSiteAsset("home.feature.renter", { width: 1200 });
  const ownerAsset = useSiteAsset("home.feature.owner", { width: 1200 });
  // Brand marks for the Organization / RealEstateAgent schema, the same card
  // and logo the share tags use, so structured data and og:image agree.
  const logoAsset = useSiteAsset("brand.logo");
  const faviconAsset = useSiteAsset("brand.favicon");
  const share = useShareImage({ social: "home" });
  const schemaLogo =
    (!logoAsset.isFallback && logoAsset.objectUrl) || (!faviconAsset.isFallback && faviconAsset.objectUrl) || null;

  // Pre-compute area → count once per data change instead of doing
  // an O(areas × properties) filter on every render.
  const propertiesByArea = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of properties) {
      map.set(p.location, (map.get(p.location) ?? 0) + 1);
    }
    return map;
  }, [properties]);

  const { areas } = useAreas();
  const sortedAreas = useMemo(() => [...areas].sort((a, b) => a.name.localeCompare(b.name)), [areas]);

  const handleListYourHome = () => {
    setIntent("owner");
    navigate(user ? "/dashboard" : "/owner-onboarding");
  };

  const handleFindYourHome = () => {
    setIntent("renter");
  };

  return (
    <div>
      <Seo
        title={copy.t(seoTitleKey("home"))}
        description={copy.t(seoDescriptionKey("home"))}
        path="/"
        social="home"
        jsonLd={[
          organizationSchema(tenant, { logo: schemaLogo }),
          webSiteSchema(tenant),
          realEstateAgentSchema(tenant, { image: share.url }),
          siteFaqSchema(tenant),
        ]}
      />
      {/* HERO */}
      <section
        className={`relative flex items-center justify-center overflow-hidden bg-charcoal ${
          useHeroVideo
            ? "aspect-[9/16] min-h-[600px] max-h-screen sm:aspect-auto sm:h-screen sm:max-h-none"
            : "h-[100svh] min-h-[600px]"
        }`}
        style={{
          // Brand-tinted gradient fallback so the hero never renders as a
          // bare grey block on mobile cellular connections where the 33 MB
          // video file is throttled or refused autoplay by Safari.
          backgroundImage:
            "radial-gradient(120% 80% at 30% 20%, hsl(19 35% 28%) 0%, hsl(0 0% 14%) 60%, hsl(0 0% 8%) 100%)",
        }}
      >
        {/* Static hero image. The earlier <video> sourced /videos/hero-bg.mp4
            (35MB) which either 404'd in production (PSI Run 3 showed
            ERR_CONNECTION_FAILED) or would have annihilated LCP if it
            had loaded. A 227KB JPG with high fetchpriority is the LCP
            now; the radial-gradient parent background paints first for
            instant fill. */}
        {useHeroVideo ? (
          <HeroVideo
            src={heroVideoAsset.src}
            poster={!heroAsset.isFallback && heroAsset.src ? heroAsset.src : undefined}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : !heroAsset.isFallback && heroAsset.src ? (
          <img
            src={heroAsset.src}
            srcSet={heroSrcSet}
            sizes="100vw"
            alt=""
            aria-hidden="true"
            loading="eager"
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : null}
        <div className="absolute inset-0 bg-charcoal/40" />

        <div className="relative z-10 text-center px-4 max-w-3xl animate-fade-in">
          <p className="font-sans font-semibold text-white tracking-[0.12em] uppercase leading-[1.2] mb-4 drop-shadow-md" style={{ fontSize: "clamp(1.25rem, 2.5vw, 1.75rem)" }}>{copy.t("home.hero.eyebrow")}</p>
          <h1
            className="font-serif italic text-white leading-[1.1] drop-shadow"
            style={{ fontSize: "clamp(3.5rem, 10vw, 7rem)" }}
          >
            {tenant.cityLabel}
          </h1>
          <p className="font-sans font-semibold text-white tracking-[0.12em] uppercase mt-4 drop-shadow-md" style={{ fontSize: "clamp(1rem, 2vw, 1.5rem)" }}>
            {tenant.regionLabel}
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 mt-12 w-full max-w-md sm:max-w-none mx-auto">
            <Link to="/properties" onClick={handleFindYourHome} className="sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full px-7 py-6 text-[13px] tracking-wide font-medium bg-transparent border-white text-white hover:bg-white/10 hover:text-white">
                {copy.t("home.hero.ctaFind")}
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              onClick={handleListYourHome}
              className="w-full sm:w-auto rounded-full px-7 py-6 text-[13px] tracking-wide font-medium bg-transparent border-white text-white hover:bg-white/10 hover:text-white"
            >
              {copy.t("home.hero.ctaList")}
            </Button>
          </div>

          {/* <div className="mt-10 sm:mt-12">
            <AlertSignup variant="hero" source="home_hero" />
          </div> */}
        </div>
      </section>

      {/* Three real numbers, each shown once believable (backlog b29bf95c). */}
      <StatBar className="container mx-auto px-4 py-8 sm:py-10" />

      {/* THE PROBLEM */}
      <section className="py-10 sm:py-12 bg-popover">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <h2 className="font-serif italic text-[26px] sm:text-[34px] font-normal mb-6 leading-[1.15] tracking-tight">
            {copy.t("home.problem.heading")}
          </h2>
          <p className="text-muted-foreground leading-relaxed text-[15px] break-words">
            {copy.t("home.problem.body")}
          </p>
        </div>
      </section>

      {/* THE SOLUTION */}
      <section className="py-10 sm:py-12 bg-background">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="font-serif text-2xl sm:text-[28px] font-medium mb-10 leading-snug">
            {copy.t("home.solution.heading")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-12">
            {[
              { icon: <CheckCircle className="h-6 w-6 text-primary" />, title: copy.t("home.solution.1.title"), desc: copy.t("home.solution.1.desc") },
              { icon: <Calendar className="h-6 w-6 text-primary" />, title: copy.t("home.solution.2.title"), desc: copy.t("home.solution.2.desc") },
              { icon: <Users className="h-6 w-6 text-primary" />, title: copy.t("home.solution.3.title"), desc: copy.t("home.solution.3.desc") },
            ].map((item) => (
              <div key={item.title} className="flex flex-col items-center text-center">
                {item.icon}
                <h3 className="text-xl mt-4 mb-2 font-medium tracking-tight">{item.title}</h3>
                <p className="text-[14px] text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR RENTERS */}
      <section className="py-10 sm:py-14 bg-popover">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[12px] uppercase tracking-[0.15em] text-primary font-medium mb-4">{copy.t("home.renters.eyebrow")}</p>
              <h2 className="font-serif text-2xl sm:text-[28px] font-medium mb-6 leading-snug">{copy.t("home.renters.heading")}</h2>
              <div className="space-y-4">
                {copy.list("home.renters.bullets").map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span className="min-w-0 break-words text-[15px] text-muted-foreground leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/properties" onClick={handleFindYourHome} className="inline-block mt-8">
                <Button size="lg" className="text-[13px] px-8">{copy.t("home.renters.cta")}</Button>
              </Link>
            </div>
            <div className={`rounded-card ${FRAME.feature} overflow-hidden bg-muted`}>
              {!renterAsset.isFallback && renterAsset.src ? (
                <img
                  src={renterAsset.src}
                  alt={renterAsset.alt || "For renters"}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* FOR OWNERS */}
      <section className="py-10 sm:py-14 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div className={`order-2 md:order-1 rounded-card ${FRAME.feature} overflow-hidden bg-muted`}>
              {!ownerAsset.isFallback && ownerAsset.src ? (
                <img
                  src={ownerAsset.src}
                  alt={ownerAsset.alt || "For owners"}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : null}
            </div>
            <div className="order-1 md:order-2">
              <p className="text-[12px] uppercase tracking-[0.15em] text-accent font-medium mb-4">{copy.t("home.owners.eyebrow")}</p>
              <h2 className="font-serif text-2xl sm:text-[28px] font-medium mb-6 leading-snug">{copy.t("home.owners.heading")}</h2>
              <div className="space-y-4">
                {copy.list("home.owners.bullets").map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                    <span className="min-w-0 break-words text-[15px] text-muted-foreground leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              <Button
                size="lg"
                onClick={handleListYourHome}
                className="mt-8 text-[13px] px-8 bg-accent text-accent-foreground hover:bg-accent/90"
              >
                {copy.t("home.owners.cta")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-10 sm:py-14 bg-secondary">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="font-serif text-2xl sm:text-[28px] font-medium mb-16 leading-snug">{copy.t("home.how.heading")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div className="text-left">
              <p className="text-[12px] uppercase tracking-[0.15em] text-primary font-medium mb-6">{copy.t("home.renters.eyebrow")}</p>
              <div className="space-y-8">
                {[
                  { step: "1", title: copy.t("home.how.renter.1.title"), desc: copy.t("home.how.renter.1.desc") },
                  { step: "2", title: copy.t("home.how.renter.2.title"), desc: copy.t("home.how.renter.2.desc") },
                  { step: "3", title: copy.t("home.how.renter.3.title"), desc: copy.t("home.how.renter.3.desc") },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[13px] font-medium shrink-0">{item.step}</div>
                    <div>
                      <h4 className="font-medium text-[15px]">{item.title}</h4>
                      <p className="text-[14px] text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-left">
              <p className="text-[12px] uppercase tracking-[0.15em] text-accent font-medium mb-6">{copy.t("home.owners.eyebrow")}</p>
              <div className="space-y-8">
                {[
                  { step: "1", title: copy.t("home.how.owner.1.title"), desc: copy.t("home.how.owner.1.desc") },
                  { step: "2", title: copy.t("home.how.owner.2.title"), desc: copy.t("home.how.owner.2.desc") },
                  { step: "3", title: copy.t("home.how.owner.3.title"), desc: copy.t("home.how.owner.3.desc") },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <div className="h-8 w-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-[13px] font-medium shrink-0">{item.step}</div>
                    <div>
                      <h4 className="font-medium text-[15px]">{item.title}</h4>
                      <p className="text-[14px] text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      {featured.length > 0 && (
        <section className="py-20 sm:py-24 bg-popover">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-serif text-2xl font-medium">{copy.t("home.featured.heading")}</h2>
              <Link to="/properties" className="text-[13px] text-primary font-medium flex items-center gap-1 hover:underline">
                {copy.t("home.featured.viewAll")} <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {featured.map((p) => (
                <PropertyCard key={p.id} property={p} isFavorite={isFavorite(p.id)} onToggleFavorite={toggleFavorite} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Areas */}
      <section className="py-20 sm:py-24 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="font-serif text-2xl font-medium mb-10 text-center">{copy.t("home.areas.heading")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {sortedAreas.map((a) => (
              <Link key={a.slug} to={`/areas/${a.slug}`} className="group bg-card rounded-xl border p-6 text-center hover:border-primary transition-colors shadow-soft hover:shadow-elevated">
                <MapPin className="h-4 w-4 text-primary mx-auto mb-2" />
                <h3 className="font-serif text-[14px]">{a.name}</h3>
                <p className="text-[12px] text-muted-foreground mt-1">
                  {(() => {
                    const n = propertiesByArea.get(a.name) ?? 0;
                    return `${n} ${n === 1 ? "property" : "properties"}`;
                  })()}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Collections, intent pages; renders nothing until one is published */}
      {collections.length > 0 && (
        <section className="pb-20 sm:pb-24 bg-background">
          <div className="container mx-auto px-4">
            <CollectionLinks collections={collections} properties={properties} ctx={browseCtx} variant="cards" heading="Browse by need" />
          </div>
        </section>
      )}

      {/* Trust */}
      <section className="py-16 sm:py-20 bg-secondary">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <Shield className="h-7 w-7 text-primary mx-auto mb-4" />
          <h2 className="font-serif text-2xl font-medium mb-3">{copy.t("home.trust.heading")}</h2>
          <p className="text-muted-foreground text-[14px] leading-relaxed max-w-lg mx-auto break-words">
            {copy.t("home.trust.body")}
          </p>
        </div>
      </section>

      {/* Renter AI concierge, test placement low on the page (renders only
          when enabled for this tenant; moves up on command later). */}
      <Suspense fallback={null}>
        <ConciergeChat />
      </Suspense>

      {/* Detailed alert preferences, alternate path for users who scroll
          the full page and want to specify area/bedrooms/budget. */}
      <section className="py-16 sm:py-20 bg-popover">
        <div className="container mx-auto px-4">
          <AlertSignup variant="footer" source="home_footer" promptSignupAfter />
        </div>
      </section>

      <AuthPromptDialog open={authPromptOpen} onOpenChange={setAuthPromptOpen} />
    </div>
  );
};

export default Index;
